
import { computed, onMounted, ref, watch } from 'vue';
import IftaLabel from 'primevue/iftalabel';
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import Password from 'primevue/password';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import InputOtp from 'primevue/inputotp';
import ProgressSpinner from 'primevue/progressspinner';
import Checkbox from 'primevue/checkbox';
import CheckboxGroup from 'primevue/checkboxgroup';
import Message from 'primevue/message';
import Toast from 'primevue/toast';

import { showVuePopup } from '../utils/showVuePopup';
import ForgotPassword from './ForgotPassword.vue';
import { external } from '../apis/external';
import { wp } from '../apis/wp';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { useWebStore } from '../stores/web';
import { useAuthStore } from '../stores/auth';
import Bank from '../utils/bank';
import BankDetail from '../components/card/BankDetail.vue';

interface Options {
	marketingRefId: string;
	affiliationRefId: string;
	recommendRefId: string;
}
interface BankList {
	userId: number
	id: number
	title: string
	completed: boolean
}

interface RecommendOption {
	name: string;
	value: string;
}

export default {
	name: 'Register',
	components: {
		Toast,
		Message,
		IftaLabel,
		InputText,
		InputMask,
		Password,
		Select,
		Divider,
		InputOtp,
		Checkbox,
		CheckboxGroup,
		ProgressSpinner,
		Button,
	},
	props: {
		close: Function,
		options: {
			type: Object as () => Options,
			required: false,
			default: () => ({ marketingRefId: '', affiliationRefId: '', recommendRefId: '' }),
		},
	},
	setup(props) {
		const auth = useAuthStore();

		const web = useWebStore();
		web.hydrate();
		const { webPermission, managerConfig } = storeToRefs(web);

		// Manager-driven register config (operator source of truth). Defaults
		// preserve the theme's prior behavior when the manager is unreachable:
		// registration open, bank account required.
		const registerEnabled = computed<boolean>(() => managerConfig.value?.register?.enabled ?? true);
		const requireBank = computed<boolean>(() => managerConfig.value?.register?.bankAccount ?? true);

		// Resolve the verify UI (OTP=1 / CAPTCHA=2) from the manager synchronously
		// at setup — before the first render — so the correct block paints straight
		// away and there's no CAPTCHA flash before OTP. 0 = manager didn't specify,
		// in which case onMounted falls back to the WP option and reveals the block.
		const mgrVerifyMode = managerConfig.value?.register?.verifyMethod
			? (managerConfig.value.register.verifyMethod === 'otp' ? 1 : 2)
			: 0;
		const modeResolved = ref(mgrVerifyMode !== 0);

		const toast = useToast();

		const banks = ref<BankList[]>([]);
		const recommends = ref<RecommendOption[]>([]);
		const loading = ref(false);

		const mkId = ref<string>((props.options.marketingRefId ?? '').trim());
		const afId = ref<string>((props.options.affiliationRefId ?? '').trim());
		const rmId = ref<string>((props.options.recommendRefId ?? '').trim());

		const form = ref({
			mode: mgrVerifyMode || 2,
			phone: { value: '', invalid: false, msgErr: '' },
			password: { value: '', invalid: false, msgErr: '' },
			otp: { ref: '', expired: '', code: '' },
			captcha: { recId: '', image: '', code: '' },
			recommend: { value: 'Google', invalid: false, msgErr: '' },
			recommendOther: { value: '', invalid: false, msgErr: '' },
			firstName: { value: '', invalid: false, msgErr: '' },
			lastName: { value: '', invalid: false, msgErr: '' },
			bankNumber: { value: '', invalid: false, msgErr: '' },
			bankName: { value: '', invalid: false, msgErr: '' },
			btn: { disabled: false, loading: false },
		});

		const hasRefId = computed(() => {
			const m = (mkId.value ?? '').trim();
			const a = (afId.value ?? '').trim();
			const r = (rmId.value ?? '').trim();
			return m !== '' || a !== '' || r !== '';
		});

		const btnSubmitDisabled = computed(() => {
			const phoneValid = form.value.phone.value.length === 10;
			const passLen = form.value.password.value.length;
			const passValid = passLen >= 6 && passLen <= 30;
			return phoneValid && passValid;
		});

		const findOptionValue = (val: string) =>
			recommends.value.find(o => o.value === val)?.value ?? '';

		function syncRecommendByProps() {
			// ถ้ารายการยังไม่มา ให้รอ
			if (!recommends.value || recommends.value.length === 0) return;

			const hasAff = (afId.value ?? '').trim() !== '';
			const hasMkt = (mkId.value ?? '').trim() !== '';
			const hasRec = (rmId.value ?? '').trim() !== '';

			if (hasAff) {
				// มี affiliationRefId → บังคับเป็น "เพื่อนแนะนำ"
				const forced = findOptionValue('เพื่อนแนะนำ') || 'เพื่อนแนะนำ';
				form.value.recommend.value = forced;
				form.value.recommend.invalid = false;
				form.value.recommend.msgErr = '';
			} else if (hasMkt || hasRec) {
				// มี marketingRefId → บังคับเป็น "โฆษณาอื่น ๆ"
				const forced = findOptionValue('โฆษณาอื่น ๆ') || 'โฆษณาอื่น ๆ';
				form.value.recommend.value = forced;
				form.value.recommend.invalid = false;
				form.value.recommend.msgErr = '';
			} else {
				// ไม่มี refId → default เป็น "Google"
				const def = findOptionValue('Google') || 'Google';
				if (!form.value.recommend.value) {
					form.value.recommend.value = def;
				}
				form.value.recommend.invalid = false;
				form.value.recommend.msgErr = '';
			}
		}

		const handleBankList = async () => {
		  try {
			  const resp = await external.bankList({data: null})

			  if (resp.code !== '0') throw new Error(resp.message)

			  banks.value = resp.result?.banks || []

		  } catch (error: any) {

		  }
		}

		const handleRecommendList = async () => {
			try {
				const resp = await external.recommendList({ data: null });
				if (resp.code !== '0') throw new Error(resp.message);
				// สมมติ backend ส่งรูปแบบ { name, value } อยู่แล้ว
				recommends.value = resp.result?.recommend || [];
				// sync หลังได้รายการ
				syncRecommendByProps();
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาด',
					detail: error.message || error,
					life: 3000,
				});
			}
		};

		const handleWpRegisterMode = async () => {
			try {
				const resp = await wp.optionKey('opt-switch-register-mode');
				if (resp.code !== '0') throw new Error(resp.message);
				form.value.mode = parseInt(resp.result);
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาด',
					detail: error.message || error,
					life: 3000,
				});
			}
		};

		const handleCaptcha = async () => {
			try {
				const resp = await external.captcha({ data: null });
				if (resp.code !== '0') throw new Error(resp.message);
				form.value.captcha = resp.result;
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาด',
					detail: error.message || error,
					life: 3000,
				});
			}
		};

		const clickRefreshCaptcha = async () => {
			form.value.captcha = { image: '', recId: '', code: '' };
			await handleCaptcha();
		};

		const handleOTP = async () => {
			try {
				const resp = await external.otp({ phone: form.value.phone.value });
				if (resp.code !== '0') throw new Error(resp.message);
				form.value.otp = resp.result;
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'OTP ล้มเหลว',
					detail: error.message || error,
					life: 3000,
				});
			}
		};

		const handleSubmit = async () => {
			form.value.btn.loading = true;
			form.value.phone.invalid = false;
			form.value.password.invalid = false;

			const phone = form.value.phone.value;
			const password = form.value.password.value;

			if (phone === '' || phone.length !== 10) {
				form.value.phone.invalid = true;
				form.value.phone.msgErr = 'เบอร์โทรศัพท์ไม่ถูกต้อง';
				form.value.btn.loading = false;
				return;
			}

			if (password.length < 6 || password.length > 30) {
				form.value.password.invalid = true;
				form.value.password.msgErr = 'รหัสผ่านต้องมี 6-30 ตัวอักษร';
				form.value.btn.loading = false;
				return;
			}

			const allowedChars = /^[A-Za-z0-9@#$%&*!_.?\-]+$/;
			if (!allowedChars.test(password)) {
				form.value.password.invalid = true;
				form.value.password.msgErr =
					'รหัสผ่านใช้ได้เฉพาะ A-Z, a-z, 0-9 และ @#$%&*!_.?-';
				form.value.btn.loading = false;
				return;
			}

			if (password.length < 6 || password.length > 30) {
				form.value.password.invalid = true;
				form.value.password.msgErr = 'รหัสผ่านต้องมี 6-30 ตัวอักษร';
				form.value.btn.loading = false;
				return;
			}

			const firstName = form.value.firstName.value;
			const lastName = form.value.lastName.value;

			if (!firstName || !lastName) {
				form.value.firstName.invalid = !firstName;
				form.value.firstName.msgErr = firstName ? '' : 'กรุณากรอกชื่อ';
				form.value.lastName.invalid = !lastName;
				form.value.lastName.msgErr = lastName ? '' : 'กรุณากรอกนามสกุล';
				form.value.btn.loading = false;
				return;
			}

			const bankName = form.value.bankName.value;
			const bankNumber = form.value.bankNumber.value;


			// Bank fields are shown+required only when the operator demands it
			// (manager register.bankAccount). Otherwise they're hidden entirely,
			// so bankName/bankNumber stay empty and this check is skipped.
			if (requireBank.value && (!bankName || !bankNumber)) {
				form.value.bankName.invalid = !bankName;
				form.value.bankName.msgErr = bankName ? '' : 'กรุณาเลือกธนาคาร';
				form.value.bankNumber.invalid = !bankNumber;
				form.value.bankNumber.msgErr = bankNumber ? '' : 'กรุณากรอกเลขบัญชี';
				form.value.btn.loading = false;
				return;
			}

			try {
				const payloadRegister = {
					username: phone,
					password: password,
					tel: phone,
					firstName: '',
					lastName: '',
					middleName: '',
					email: '',
					verifyType: form.value.mode,
					verifyRef1:
						form.value.mode === 1 ? form.value.otp.ref : form.value.captcha.recId,
					verifyRef2:
						form.value.mode === 1 ? form.value.otp.code : form.value.captcha.code,
					recommend: form.value.recommend.value,
					recommendOther: form.value.recommendOther.value,
					refId: afId.value,
					affBrand: '',
					marketingId: mkId.value,
					lineId: '',
					isTelegram: false,
					isCheckUsername: false,
					isNoRequirePhone: false,
				};

				if (payloadRegister.verifyRef1 === '' || payloadRegister.verifyRef2 === '') {
					throw new Error('กรุณากรอกข้อมูลยืนยันให้ครบถ้วน');
				}

				const resp = await external.register(payloadRegister);

				if (resp.code !== '0') throw new Error(resp.message);

				await auth.login({
					username: resp.result.username,
					password: resp.result.password,
				});

				// Only push bank details when we actually collected them — with
				// an optional bank account (requireBank=false) these may be empty.
				if (bankName && bankNumber) {
					const payloadUpdateBank = {
						firstName: firstName,
						lastName: lastName,
						middleName: '',
						bankName: bankName,
						accountNo: bankNumber,
						isAutoPromotion: true,
					}

					await external.updateBank(payloadUpdateBank);
				}

				toast.add({
					severity: 'success',
					summary: 'สำเร็จ',
					detail: 'สมัครสมาชิกเรียบร้อย',
					life: 3000,
				});

				props.close?.();

				window.location.href = '/slot';
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาด',
					detail: error,
					life: 3000,
				});
			} finally {
				form.value.btn.loading = false;
			}
		};

		const clickBtnForgotPassword = () => {
			showVuePopup(ForgotPassword, {}, {
				customClass: {
					htmlContainer: '',
				},
			});
		};

		const checkPhoneNumber = () => {
			const phone = form.value.phone.value.trim();
			const isValid = /^0[0-9]{9}$/.test(phone);
			if (!isValid) {
				form.value.phone.invalid = true;
				form.value.phone.msgErr = 'เบอร์โทรศัพท์ไม่ถูกต้อง ต้องเป็นตัวเลข 10 หลัก';
				return;
			}
			form.value.phone.invalid = false;
			form.value.phone.msgErr = '';
		};

		const checkBankNumber = () => {
			const bankNumber = form.value.bankNumber.value;
			const isValid = /^[0-9]{10,14}$/.test(bankNumber);
			if (!isValid) {
				form.value.bankNumber.invalid = true;
				form.value.bankNumber.msgErr = 'บัญชีธนาคารไม่ถูกต้อง ต้องเป็นตัวเลข 10-14 หลัก';
				return;
			}
			form.value.bankNumber.invalid = false;
			form.value.bankNumber.msgErr = '';
		}

		const checkPassword = () => {
			const password = form.value.password.value.trim();

			const allowedChars = /^[A-Za-z0-9@#$%&*!_.?\-]+$/;
			if (!allowedChars.test(password)) {
				form.value.password.invalid = true;
				form.value.password.msgErr =
					'รหัสผ่านใช้ได้เฉพาะ A-Z, a-z, 0-9 และ @#$%&*!_.?-';
				return;
			}

			if (password.length < 6 || password.length > 30) {
				form.value.password.invalid = true;
				form.value.password.msgErr = 'รหัสผ่านต้องมี 6-30 ตัวอักษร';
				form.value.btn.loading = false;
				return;
			}

			form.value.password.invalid = false;
			form.value.password.msgErr = '';
		};

		// --- Lifecycle / watchers ---
		onMounted(async () => {
			const urlParams = new URLSearchParams(window.location.search);
			const getTrim = (key: any) => (urlParams.get(key) || '').trim();
			const hasRef = urlParams.has('ref') && getTrim('ref') !== '';
			const hasMktRef = urlParams.has('marketingRef') && getTrim('marketingRef') !== '';

			if (hasRef || hasMktRef) {
				const lines = [
					'กรอกข้อมูลได้เลย!! ไม่ต้องกดลงทะเบียนซ้ำ',
					'หากไม่ขึ้นไอดีแนะนำเพื่อนติดต่อแอดมิน',
				];
				if (hasRef) lines.push(`ไอดีแนะนำเพื่อน: ${getTrim('ref')}`);
				alert(lines.join('\n'));
			}

			// The verify mode is already resolved from the manager in setup when
			// configured (modeResolved=true, no flash). Only hit the WP option as a
			// fallback, then reveal the verify block.
			if (mgrVerifyMode === 0) {
				await handleWpRegisterMode();
				modeResolved.value = true;
			}
			if (form.value.mode === 2) await handleCaptcha();
			await handleBankList();
			await handleRecommendList(); // ภายในจะเรียก syncRecommendByProps แล้ว
		});
		// อัปเดต mkId/afId เมื่อ props เปลี่ยน แล้วค่อย sync
		watch(
			() => props.options,
			(v) => {
				mkId.value = (v?.marketingRefId ?? '').trim();
				afId.value = (v?.affiliationRefId ?? '').trim();
				syncRecommendByProps();
			},
			{ deep: true }
		);

		// ถ้า list เปลี่ยน (เช่นโหลดช้ากว่า) → sync อีกครั้ง
		watch(
			() => recommends.value,
			() => syncRecommendByProps()
		);

		// ถ้าผู้ใช้เลือก "อื่นๆ" → เปิดช่องกรอกข้อความ
		watch(
			() => form.value.recommend.value,
			(val) => {
				if (val === 'อื่นๆ') {
					form.value.recommendOther.invalid = false;
				} else {
					form.value.recommendOther.value = '';
					form.value.recommendOther.invalid = false;
					form.value.recommendOther.msgErr = '';
				}
			}
		);

		return {
			form,
			banks,
			recommends,
			loading,
			hasRefId,
			registerEnabled,
			requireBank,
			modeResolved,
			handleSubmit,
			clickBtnForgotPassword,
			clickRefreshCaptcha,
			btnSubmitDisabled,
			checkPhoneNumber,
			checkPassword,
			checkBankNumber,
			handleOTP,
		};
	},
};
